import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { controlToModule, type ModuleId } from "@/lib/infrastructureV2Simulator";
import { InfrastructureControlInfoPopup } from "./InfrastructureControlInfoPopup";
import { TelecomControlFindings } from "./TelecomControlFindings";
import { resolveInfrastructureControlDisplay } from "@/configs/controlGuidance/infrastructureControlMetadata";

const sb: any = supabase as any;

type Status = "Implemented" | "Partially Implemented" | "Not Implemented" | "Not Applicable";

const STATUS_OPTIONS: Status[] = ["Implemented", "Partially Implemented", "Not Implemented", "Not Applicable"];


const STATUS_COLORS: Record<Status, string> = {
  "Implemented":           "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  "Partially Implemented": "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  "Not Implemented":       "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  "Not Applicable":        "bg-muted text-muted-foreground",
};

// Telecom modules in display order with friendly labels and the
// component_assessed values that bring each module in-scope.
// Component labels match those in InfrastructureScopeCard.COMPONENTS.
interface TelecomModuleDef {
  id: ModuleId;
  label: string;
  scopeComponents: string[];
}

const TELECOM_MODULES: TelecomModuleDef[] = [
  { id: "C1", label: "C1 · Radio Access Network",
    scopeComponents: ["RAN (Radio Access Network)", "Tower / Cell Site Infrastructure", "Shared DAS / Small Cell Infrastructure"] },
  { id: "C2", label: "C2 · Core Network",
    scopeComponents: ["Core Network (5G SBA / EPC / IMS)"] },
  { id: "C3", label: "C3 · Signaling Network",
    scopeComponents: ["Signaling (SS7 / Diameter / GTP)"] },
  { id: "C4", label: "C4 · Transport / Backhaul",
    scopeComponents: ["Transport / Backhaul", "Fiber / Fixed Access Network", "Cable Landing / Subsea", "POP / Exchange / Meet-Me-Room"] },
  { id: "C5", label: "C5 · Virtualization & Edge",
    scopeComponents: ["NFV / MEC", "Cloud / Edge Infrastructure", "Edge Sites / POPs", "Data Centre / Co-location"] },
  { id: "C6", label: "C6 · OSS / BSS",
    scopeComponents: ["OSS / BSS"] },
  { id: "C7", label: "C7 · Subscriber / Roaming / IoT",
    scopeComponents: ["Subscriber Data Stores (HSS / UDM)", "IoT / M2M Gateway", "Roaming / IPX", "Customer Edge / SIM Provisioning"] },
  // C8 (continuity) has no direct scope component. Include it whenever any
  // telecom component is assessed, so reviewers can capture DR / RTO / RPO
  // controls without needing a dedicated scope row.
  { id: "C8", label: "C8 · Service Continuity",
    scopeComponents: [] },
];

interface CatalogControl {
  control_key: string;
  control_label: string;
  category: string;
  component_type: string;
  description?: string | null;
  scoring_enabled_default: boolean;
  score_basis_version_introduced: string;
}

interface NistMapping {
  control_key: string;
  nist_function: string;
  nist_control_code: string;
}

export interface TelecomRow {
  control_key: string;
  control_label: string;
  category: string;
  status: string | null;
  reviewer_notes: string | null;
  evidence_url: string | null;
  scoring_enabled: boolean;
  add_to_findings: boolean;
}

interface Props {
  assessmentId: string;
  rows: Map<string, TelecomRow>;
  componentsAssessed: string[];
  disabled?: boolean;
  onToggleControl: (cfg: { key: string; label: string; category: string }, checked: boolean) => void;
  onUpdateField: (key: string, patch: Partial<TelecomRow>) => void;
}

// Phase 2.8.3 — eligibility is now owned by <TelecomControlFindings>, which
// auto-creates findings on transition into Partial/Not Implemented and
// auto-removes unedited template findings on transition out.

export const TelecomControlsSection: React.FC<Props> = ({
  assessmentId,
  rows,
  componentsAssessed,
  disabled,
  onToggleControl,
  onUpdateField,
}) => {
  const [catalog, setCatalog] = useState<CatalogControl[]>([]);
  const [mappings, setMappings] = useState<NistMapping[]>([]);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    (async () => {
      const [catRes, mapRes] = await Promise.all([
        sb.rpc("get_telecom_infrastructure_control_catalog"),
        sb.from("infrastructure_control_mapping").select("control_key,nist_function,nist_control_code"),
      ]);
      if (!catRes.error && Array.isArray(catRes.data)) {
        setCatalog(
          (catRes.data as any[]).filter(
            (c) => c.is_telecom_specific === true || (c.component_type && c.component_type !== "generic"),
          ),
        );
      }
      if (!mapRes.error && Array.isArray(mapRes.data)) {
        setMappings(mapRes.data as NistMapping[]);
      }
    })();
  }, []);

  // Group catalog by ModuleId via simulator's controlToModule.
  const byModule = useMemo(() => {
    const m = new Map<ModuleId, CatalogControl[]>();
    for (const c of catalog) {
      const mod = controlToModule({
        control_key: c.control_key,
        category: c.category,
        component_type: c.component_type,
        status: null,
      });
      if (!mod || mod.startsWith("B")) continue;
      const list = m.get(mod) ?? [];
      list.push(c);
      m.set(mod, list);
    }
    return m;
  }, [catalog]);

  const mappingsByKey = useMemo(() => {
    const m = new Map<string, NistMapping[]>();
    for (const x of mappings) {
      const arr = m.get(x.control_key) ?? [];
      arr.push(x);
      m.set(x.control_key, arr);
    }
    return m;
  }, [mappings]);

  // A module is in-scope when any of its scopeComponents is in componentsAssessed.
  // C8 (no scopeComponents) is in-scope whenever any telecom-related component
  // is assessed (i.e., assessment has any non-generic scope at all).
  const assessedSet = useMemo(() => new Set(componentsAssessed), [componentsAssessed]);
  const anyTelecomAssessed = useMemo(
    () => TELECOM_MODULES.some((mod) => mod.scopeComponents.some((c) => assessedSet.has(c))),
    [assessedSet],
  );

  const visibleModules = TELECOM_MODULES.filter((mod) => {
    if (mod.scopeComponents.length === 0) return anyTelecomAssessed; // C8
    return mod.scopeComponents.some((c) => assessedSet.has(c));
  });

  if (visibleModules.length === 0) {
    return (
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Telecom-Specific Controls
        </h3>
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-4 text-xs text-muted-foreground">
          Mark one or more telecom components as <em>Present + Assessed</em> in the scope card
          above to unlock the C1–C8 telecom-specific controls.
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Telecom Infrastructure Controls (C1–C8)
        </h3>
      </div>

      <div className="space-y-2">
        {visibleModules.map((mod) => {
          const controls = byModule.get(mod.id) ?? [];
          const ratedCount = controls.filter((c) => {
            const r = rows.get(c.control_key);
            return r?.status && r.status !== "Not Applicable";
          }).length;
          const isOpen = open[mod.id] ?? true;
          return (
            <Collapsible
              key={mod.id}
              open={isOpen}
              onOpenChange={(v) => setOpen((p) => ({ ...p, [mod.id]: v }))}
            >
              <div className="rounded-lg border border-border bg-card">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-muted/40 rounded-lg"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      )}
                      <span className="font-medium text-sm">{mod.label}</span>
                      <Badge variant="secondary" className="text-[10px]">
                        {controls.length} control{controls.length === 1 ? "" : "s"}
                      </Badge>
                      {ratedCount > 0 && (
                        <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-900/20 dark:text-emerald-300">
                          {ratedCount} rated
                        </Badge>
                      )}
                    </div>
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="border-t border-border p-3 space-y-3">
                    {controls.length === 0 && (
                      <div className="text-xs text-muted-foreground italic">
                        No catalog controls seeded for this module yet.
                      </div>
                    )}
                    {controls.map((c) => {
                      const row = rows.get(c.control_key);
                      const isChecked = !!row;
                      const status = (row?.status as Status | null) ?? null;
                      const nistList = mappingsByKey.get(c.control_key) ?? [];
                      // Phase 2.8.4 — Always render the resolved metadata title.
                      // Never leak the raw control_key (or a DB control_label
                      // that still equals the key) into the reviewer UI.
                      const displayTitle = resolveInfrastructureControlDisplay(c.control_key, c.control_label).title;
                      const cfg = { key: c.control_key, label: displayTitle, category: c.category };

                      return (
                        <div
                          key={c.control_key}
                          className={`rounded-md border p-3 ${
                            isChecked ? "bg-card border-primary/30" : "bg-muted/20 border-border"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={isChecked}
                              disabled={disabled}
                              onCheckedChange={(v) => onToggleControl(cfg, !!v)}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0 space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`text-sm font-medium ${isChecked ? "text-foreground" : "text-muted-foreground"}`}>
                                  {displayTitle}
                                </span>
                                <InfrastructureControlInfoPopup
                                  controlKey={c.control_key}
                                  fallbackLabel={displayTitle}
                                />
                                {nistList.map((n) => (
                                  <span
                                    key={`${n.nist_function}-${n.nist_control_code}`}
                                    className="inline-flex items-center rounded-full border border-border bg-muted/60 px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                                    title={`NIST CSF ${n.nist_function}`}
                                  >
                                    {n.nist_control_code}
                                  </span>
                                ))}
                                {isChecked && status && (
                                  <Badge variant="outline" className={`text-[11px] ${STATUS_COLORS[status]}`}>
                                    {status}
                                  </Badge>
                                )}
                              </div>

                              {c.description && (
                                <p className="text-xs text-muted-foreground">{c.description}</p>
                              )}

                              {isChecked && (
                                <div className="space-y-3 pt-1">
                                  <div className="flex flex-wrap gap-3">
                                    <div className="space-y-1">
                                      <label className="text-xs text-muted-foreground font-medium">Status</label>
                                      <Select
                                        value={row?.status ?? ""}
                                        onValueChange={(v) => onUpdateField(c.control_key, { status: v })}
                                        disabled={disabled}
                                      >
                                        <SelectTrigger className="w-[200px] h-8 text-xs">
                                          <SelectValue placeholder="Select status…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {STATUS_OPTIONS.map((opt) => (
                                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>

                                    <div className="space-y-1 flex-1 min-w-[240px]">
                                      <label className="text-xs text-muted-foreground font-medium">Evidence link (optional)</label>
                                      <Input
                                        type="url"
                                        placeholder="https://…"
                                        className="h-8 text-xs"
                                        defaultValue={row?.evidence_url ?? ""}
                                        disabled={disabled}
                                        onBlur={(e) => {
                                          const v = e.target.value.trim() || null;
                                          if (v !== (row?.evidence_url ?? null)) {
                                            onUpdateField(c.control_key, { evidence_url: v });
                                          }
                                        }}
                                      />
                                    </div>
                                  </div>

                                  <div className="space-y-1">
                                    <label className="text-xs text-muted-foreground font-medium">Reviewer notes</label>
                                    <Textarea
                                      placeholder="Observations, gaps, or context…"
                                      className="text-xs min-h-[60px]"
                                      defaultValue={row?.reviewer_notes ?? ""}
                                      disabled={disabled}
                                      onBlur={(e) => {
                                        const v = e.target.value.trim() || null;
                                        if (v !== (row?.reviewer_notes ?? null)) {
                                          onUpdateField(c.control_key, { reviewer_notes: v });
                                        }
                                      }}
                                    />
                                  </div>




                                  {/* Phase 2.8.3 — Always mount so the component
                                      can auto-create on transition to
                                      Partial/Not Implemented and auto-remove
                                      auto-seeded findings on transition to
                                      Implemented / N/A. */}
                                  <TelecomControlFindings
                                    assessmentId={assessmentId}
                                    controlKey={c.control_key}
                                    controlLabel={displayTitle}
                                    controlCode={nistList.map((n: any) => n.nist_control_code).filter(Boolean).join(", ")}
                                    controlStatus={row?.status ?? null}
                                    disabled={disabled}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          );
        })}
      </div>
    </section>
  );
};
